import { useState } from 'react';
import type { AdminUser } from '../types/api';

interface CreditTopUpDialogProps {
  user: AdminUser;
  onConfirm: (credits: number, note: string) => void;
  onCancel: () => void;
  isPending: boolean;
}

export default function CreditTopUpDialog({
  user,
  onConfirm,
  onCancel,
  isPending,
}: CreditTopUpDialogProps) {
  const [credits, setCredits] = useState('50');
  const [note, setNote] = useState('');

  const parsedCredits = Number(credits);
  const isValid =
    Number.isFinite(parsedCredits) &&
    parsedCredits > 0 &&
    parsedCredits <= 10_000 &&
    Number.isInteger(parsedCredits);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Top up credits</h3>
        <p className="text-sm text-gray-600 mb-1">
          Add purchased credits for:
        </p>
        <p className="text-sm font-medium text-gray-900 mb-4">
          {user.dbUser.email}
        </p>
        {user.credits && (
          <p className="text-sm text-gray-500 mb-4">
            Current balance:{' '}
            <span className="font-medium text-gray-900">{user.credits.balance}</span>
            {user.credits.purchasedRemaining > 0 && (
              <span className="ml-2">
                ({user.credits.purchasedRemaining} purchased)
              </span>
            )}
          </p>
        )}
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Credits to add
        </label>
        <input
          type="number"
          min={1}
          max={10000}
          step={1}
          value={credits}
          onChange={(e) => setCredits(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm mb-4"
          disabled={isPending}
        />
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Note (optional)
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. testing support"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm mb-6"
          disabled={isPending}
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(parsedCredits, note.trim())}
            disabled={isPending || !isValid}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {isPending ? 'Adding…' : 'Add credits'}
          </button>
        </div>
      </div>
    </div>
  );
}
